import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    GetDocumentIndexStatus,
    hydrateIndexingFiles,
    markFilesIndexingFailed,
    pruneExpiredIndexingState,
} from '../redux/documentsSlice';

const POLL_INTERVAL_MS = 8000;

/**
 * Polls Azure Search (keyword + RAG indexes) for recently uploaded VDR files.
 * The spinner clears once the Azure indexer has the file (keyword or RAG).
 * Files still missing from both indexes can be marked failed so the user can retry.
 */
const useVdrIndexStatusPolling = () => {
    const dispatch = useDispatch();
    const indexingFiles = useSelector(state => state?.documents?.indexingFiles || []);
    const trayItems = useSelector(state => state?.documents?.uploadTray?.items || []);
    const { selectedProject } = useSelector(state => state?.projectData);
    const inFlightRef = useRef(false);

    const pollTargets = useMemo(() => {
        const byId = new Map();
        indexingFiles.forEach(item => {
            if (item?.id == null) return;
            const startedAt = Number(item.startedAt) || 0;
            byId.set(Number(item.id), {
                id: Number(item.id),
                wsId: item?.wsId != null ? Number(item.wsId) : null,
                startedAt,
                status: item?.status === 'failed' ? 'failed' : 'indexing',
            });
        });
        trayItems.forEach(item => {
            if (item?.status !== 'indexing' || item?.documentId == null) {
                return;
            }
            const id = Number(item.documentId);
            if (!byId.has(id)) {
                byId.set(id, {
                    id,
                    wsId: item?.wsId != null ? Number(item.wsId) : null,
                    // Never fall back to Date.now() — that reset the TTL on every remount
                    // and left "indexing" / "processing" stuck for days.
                    startedAt: Number(item.startedAt) || 0,
                });
            }
        });
        return [...byId.values()];
    }, [indexingFiles, trayItems]);

    useEffect(() => {
        dispatch(hydrateIndexingFiles());
    }, [dispatch, selectedProject?.id]);

    useEffect(() => {
        if (!pollTargets.length) return undefined;

        const projectId = selectedProject?.id || localStorage.getItem('project_id');
        if (!projectId) return undefined;

        const poll = async () => {
            if (inFlightRef.current) return;
            inFlightRef.current = true;
            try {
                dispatch(pruneExpiredIndexingState());
                const timedOutIds = [];
                const active = [];
                pollTargets.forEach(item => {
                    if (item.status === 'failed') {
                        active.push(item);
                    } else if (item.wsId == null || Number.isNaN(Number(item.wsId))) {
                        timedOutIds.push(item.id);
                    } else {
                        active.push(item);
                    }
                });
                if (timedOutIds.length) {
                    dispatch(markFilesIndexingFailed(timedOutIds));
                }
                if (!active.length) return;

                const byWs = active.reduce((acc, item) => {
                    if (item.wsId == null || Number.isNaN(Number(item.wsId))) return acc;
                    const key = String(item.wsId);
                    if (!acc[key]) acc[key] = [];
                    acc[key].push(item.id);
                    return acc;
                }, {});

                await Promise.all(
                    Object.entries(byWs).map(([wsId, fileIds]) =>
                        dispatch(
                            GetDocumentIndexStatus({
                                wsId,
                                fileIds,
                                projectId,
                            }),
                        ),
                    ),
                );
            } finally {
                inFlightRef.current = false;
            }
        };

        poll();
        const timer = setInterval(poll, POLL_INTERVAL_MS);
        return () => clearInterval(timer);
    }, [dispatch, pollTargets, selectedProject?.id]);
};

export default useVdrIndexStatusPolling;
